/**
 * Customer-facing Review proposal tab model (systems → overview / rooms / services / general).
 */
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import type { DtoolsReviewPrimary, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import {
  buildSystemOverviewRender,
  buildSystemHighlightsRender,
  readDtoolsSystemContentMap,
  resolveDtoolsSystemContentKey,
  systemContentKey,
  type DtoolsSystemContent,
} from "./dtoolsProposalContent";
import {
  buildSystemDisplayNameMap,
  readDtoolsProposalPresentation,
  type DtoolsProposalPresentation,
} from "./dtoolsProposalPresentation";
import { buildCustomerFacingOptionOverview } from "./dtoolsCustomerOptionCopy";
import type { DtoolsSystemClassificationsMap } from "./dtoolsSystemClassification";
import { resolveSystemClassification } from "./dtoolsSystemClassification";
import { resolveCustomerFacingSystemTitle } from "./dtoolsSystemClassification";
import type { VoSystemOverviewAnnotation } from "./variationOrderSystemOverview";

export type DtoolsReviewTabKind = "overview" | "room" | "services" | "general";

export type DtoolsReviewTabModel = {
  key: string;
  kind: DtoolsReviewTabKind;
  label: string;
  primaries: DtoolsReviewPrimary[];
  serviceLines: DtoolsStagingLine[];
};

export type DtoolsReviewRoomTotal = {
  name: string;
  total: number;
};

export type DtoolsReviewSystemOverview = {
  description: string;
  highlights: string[];
  roomNames: string[];
  roomTotals: DtoolsReviewRoomTotal[];
  servicesTotal: number;
  systemTotal: number;
  /** Living proposal annotation when this overview was updated by an approved VO. */
  variationOrderAnnotation?: VoSystemOverviewAnnotation | null;
};

export type DtoolsReviewBuildContext = {
  systemContentMap?: Record<string, DtoolsSystemContent>;
  presentation?: DtoolsProposalPresentation;
  systemClassifications?: DtoolsSystemClassificationsMap;
  /** Precomputed display names by system content key. */
  systemDisplayNames?: Map<string, string>;
  /** Approved VO overview annotations by system content key. */
  systemOverviewAnnotations?: Record<string, VoSystemOverviewAnnotation>;
};

export type DtoolsReviewSystemView = {
  system: DtoolsReviewSystem;
  tabs: DtoolsReviewTabModel[];
  overview: DtoolsReviewSystemOverview;
  defaultTabKey: string;
};

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function sumLine(line: DtoolsStagingLine): number {
  if (line.variationOrderAnnotation?.kind === "removed") return 0;
  return roundMoney(line.lineTotal);
}

export function sumPrimaryBlock(block: DtoolsReviewPrimary): number {
  return roundMoney(sumLine(block.line) + block.accessories.reduce((s, a) => s + sumLine(a), 0));
}

export function sumPrimaryBlocks(blocks: DtoolsReviewPrimary[]): number {
  return roundMoney(blocks.reduce((s, b) => s + sumPrimaryBlock(b), 0));
}

/** Σ(line.lineTotal) for customer buckets on lines placed in this hierarchy system. */
export function sumCustomerLineTotalsInHierarchySystem(system: DtoolsReviewSystem): number {
  const customerBuckets = new Set(["commercial", "discount", "service", "cable"]);
  let sum = 0;
  const addLine = (line: DtoolsStagingLine) => {
    if (!customerBuckets.has(line.bucket)) return;
    if (line.variationOrderAnnotation?.kind === "removed") return;
    sum += Math.max(0, Number(line.lineTotal) || 0);
  };
  const addBlock = (block: DtoolsReviewPrimary) => {
    addLine(block.line);
    for (const accessory of block.accessories) addLine(accessory);
  };
  for (const room of system.rooms) {
    for (const block of room.primaries) addBlock(block);
  }
  for (const block of system.systemLevelPrimaries) addBlock(block);
  return roundMoney(sum);
}

export function isServiceLine(line: DtoolsStagingLine): boolean {
  if (line.bucket !== "service") return false;
  // Parent-linked hardware accessories stay under their primary — not the Services tab.
  if (line.isAccessory || line.parentId) return false;
  return true;
}

export function productPrimaryBlocks(blocks: DtoolsReviewPrimary[]): DtoolsReviewPrimary[] {
  return blocks.filter((b) => !isServiceLine(b.line));
}

export function collectServiceLines(blocks: DtoolsReviewPrimary[]): DtoolsStagingLine[] {
  const lines: DtoolsStagingLine[] = [];
  for (const block of blocks) {
    if (isServiceLine(block.line)) lines.push(block.line);
    for (const acc of block.accessories) {
      if (isServiceLine(acc)) lines.push(acc);
    }
  }
  return lines;
}

export function buildSystemOverviewDescription(
  systemName: string,
  _roomNames: string[],
  system?: DtoolsReviewSystem,
): string {
  if (system) return buildCustomerFacingOptionOverview(system, systemName);
  return `${systemName} covers the equipment quoted for this home-technology scope.`;
}

export function buildDtoolsReviewSystemView(
  system: DtoolsReviewSystem,
  context?: DtoolsReviewBuildContext,
  optionIndex = 0,
): DtoolsReviewSystemView {
  const roomTabs: DtoolsReviewTabModel[] = [];
  const roomTotals: DtoolsReviewRoomTotal[] = [];
  const roomNames: string[] = [];
  let servicesTotal = 0;
  let generalTab: DtoolsReviewTabModel | null = null;

  for (const room of system.rooms) {
    const roomProducts = productPrimaryBlocks(room.primaries);
    const roomServices = collectServiceLines(room.primaries);

    if (roomProducts.length > 0) {
      const label = room.name?.trim() || "Room";
      roomNames.push(label);
      const total = sumPrimaryBlocks(roomProducts);
      roomTotals.push({ name: label, total });
      roomTabs.push({
        key: `room:${room.locationId ?? label}`,
        kind: "room",
        label,
        primaries: roomProducts,
        serviceLines: [],
      });
    }

    if (roomServices.length > 0) {
      servicesTotal = roundMoney(servicesTotal + roomServices.reduce((s, l) => s + sumLine(l), 0));
    }
  }

  const generalProducts = productPrimaryBlocks(system.systemLevelPrimaries);
  const generalServices = collectServiceLines(system.systemLevelPrimaries);

  if (generalProducts.length > 0) {
    const total = sumPrimaryBlocks(generalProducts);
    roomTotals.push({ name: "Project-Wide Items", total });
    generalTab = {
      key: "general",
      kind: "general",
      label: "Project-Wide Items",
      primaries: generalProducts,
      serviceLines: [],
    };
  }

  const allServiceLines = [
    ...system.rooms.flatMap((r) => collectServiceLines(r.primaries)),
    ...generalServices,
  ];

  let servicesTab: DtoolsReviewTabModel | null = null;
  if (allServiceLines.length > 0) {
    servicesTotal = roundMoney(allServiceLines.reduce((s, l) => s + sumLine(l), 0));
    servicesTab = {
      key: "services",
      kind: "services",
      label: "Services",
      primaries: [],
      serviceLines: allServiceLines,
    };
  }

  const systemTotal = roundMoney(
    roomTotals.reduce((s, r) => s + r.total, 0) + servicesTotal,
  );

  const systemContentMap = context?.systemContentMap ?? {};
  const displayName = resolveSystemDisplayLabel(system, context, optionIndex);
  const overviewRender = buildSystemOverviewRender({
    systemId: system.systemId,
    systemName: displayName,
    roomNames,
    system,
    systemContentMap,
  });
  const highlights = buildSystemHighlightsRender({
    systemId: system.systemId,
    systemName: displayName,
    system,
    systemContentMap,
  });

  const overview: DtoolsReviewSystemOverview = {
    description: overviewRender.body,
    highlights,
    roomNames,
    roomTotals,
    servicesTotal,
    systemTotal,
    variationOrderAnnotation:
      context?.systemOverviewAnnotations?.[
        resolveDtoolsSystemContentKey({ systemId: system.systemId, name: system.name })
      ] ??
      context?.systemOverviewAnnotations?.[systemContentKey(system.systemId, system.name)] ??
      null,
  };

  const tabs: DtoolsReviewTabModel[] = [
    {
      key: "overview",
      kind: "overview",
      label: "Overview",
      primaries: [],
      serviceLines: [],
    },
    ...roomTabs,
    ...(servicesTab ? [servicesTab] : []),
    ...(generalTab ? [generalTab] : []),
  ];

  return {
    system,
    tabs,
    overview,
    defaultTabKey: "overview",
  };
}

export function buildDtoolsReviewSystemViews(
  systems: DtoolsReviewSystem[],
  context?: DtoolsReviewBuildContext,
): DtoolsReviewSystemView[] {
  const presentation = context?.presentation ?? {};
  const systemDisplayNames =
    context?.systemDisplayNames ??
    buildSystemDisplayNameMap(systems, presentation, context?.systemClassifications);
  const enrichedContext: DtoolsReviewBuildContext = {
    ...context,
    presentation,
    systemDisplayNames,
  };
  return systems.map((system, index) => buildDtoolsReviewSystemView(system, enrichedContext, index));
}

export function enrichDtoolsReviewBuildContext(
  systems: DtoolsReviewSystem[],
  context?: DtoolsReviewBuildContext,
): DtoolsReviewBuildContext {
  const presentation = context?.presentation ?? {};
  return {
    ...context,
    presentation,
    systemDisplayNames:
      context?.systemDisplayNames ??
      buildSystemDisplayNameMap(systems, presentation, context?.systemClassifications),
  };
}

export function resolveSystemDisplayLabel(
  system: DtoolsReviewSystem,
  context?: DtoolsReviewBuildContext,
  optionIndex = 0,
): string {
  const key = systemContentKey(system.systemId, system.name);
  const classification = resolveSystemClassification(
    context?.systemClassifications,
    system.systemId,
    system.name,
  );
  if (context?.systemDisplayNames?.has(key)) {
    return context.systemDisplayNames.get(key)!;
  }
  return resolveCustomerFacingSystemTitle(system, {
    classification,
    presentationSystemDisplayNames: context?.presentation?.systemDisplayNames,
    optionIndex,
  });
}

export type DtoolsReviewProposalTotals = {
  subtotal: number;
  adjustmentsTotal: number;
  tax: number | null;
  finalTotal: number;
  currency: string;
  systemTotals: Array<{ name: string; total: number }>;
};

export function buildDtoolsReviewProposalTotals(input: {
  systems: DtoolsReviewSystem[];
  footerAdjustments: DtoolsStagingLine[];
  reconciliation?: {
    linesCustomerTotal?: number;
    headerSubtotal?: number | null;
    headerTax?: number | null;
    headerTotal?: number | null;
    currency?: string;
  } | null;
  projectTotal?: number | null;
  /** Prefer reconciliation.headerTotal even when it diverges from line arithmetic (approved VO contract). */
  preferHeaderTotal?: boolean;
}): DtoolsReviewProposalTotals {
  const views = buildDtoolsReviewSystemViews(input.systems);
  const systemTotals = views.map((v) => ({ name: v.system.name, total: v.overview.systemTotal }));
  const systemsSum = roundMoney(systemTotals.reduce((s, r) => s + r.total, 0));
  const adjustmentsTotal = roundMoney(
    input.footerAdjustments.reduce((s, l) => s + sumLine(l), 0),
  );
  const subtotal =
    input.reconciliation?.linesCustomerTotal != null
      ? roundMoney(input.reconciliation.linesCustomerTotal)
      : systemsSum;
  const tax = input.reconciliation?.headerTax ?? null;
  const arithmetic = roundMoney(subtotal + (tax ?? 0) + adjustmentsTotal);
  const headerTotal = input.reconciliation?.headerTotal;
  const finalTotal =
    headerTotal != null &&
    (input.preferHeaderTotal === true || Math.abs(headerTotal - arithmetic) <= 0.01)
      ? roundMoney(headerTotal)
      : input.projectTotal != null
        ? roundMoney(input.projectTotal)
        : arithmetic;

  return {
    subtotal: roundMoney(subtotal),
    adjustmentsTotal,
    tax: tax != null ? roundMoney(tax) : null,
    finalTotal: roundMoney(finalTotal),
    currency: input.reconciliation?.currency ?? "SGD",
    systemTotals,
  };
}
